import { useEffect, useRef, useState } from 'react';
import dateTimezone from '@/utils/date-timezone';
import { ListingReview } from '@/utils/platform-api/__generated__/graphql';
import { formatDate } from 'date-fns';
import { ClientBrandConfig } from 'types/brand-config';

import StarRating from '../star-rating';
import { getReviewSourceDisplay } from './review-source';

const Review: React.FC<{
  review: ListingReview;
  currentView: boolean;
  brand: ClientBrandConfig;
}> = ({ review, currentView, brand }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsClamped(element.scrollHeight > element.clientHeight);
    }
  }, []);

  const toggleExpanded = () => {
    if (!isClamped) {
      return;
    }
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (expanded && !currentView) {
      toggleExpanded();
    }
  }, [currentView]);

  const sourceDisplay = review.source
    ? getReviewSourceDisplay(review.source)
    : null;

  return (
    <div className="flex h-full w-full flex-col gap-[0.5rem] self-stretch rounded-lg">
      <div className="flex flex-col">
        {review.reviewer && (
          <span className="ut-md pb-1 leading-6 text-black-1">
            {review.reviewer}
          </span>
        )}
        <div className="flex flex-1 items-center">
          <StarRating rating={review.rating} />
          <span className="ut-xs !font-normal !leading-4 !text-gray-5">
            &nbsp;•&nbsp;
            {formatDate(
              dateTimezone(review.submitted_date, brand.timezone),
              'MMMM yyyy'
            )}
          </span>
        </div>
      </div>
      <blockquote>
        <p ref={textRef} className="ut-sm font-normal">
          {review.text}
        </p>
      </blockquote>
      {sourceDisplay && (
        <div className="flex items-center gap-2">
          {sourceDisplay.iconClass && sourceDisplay.iconBackground ? (
            <span
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: sourceDisplay.iconBackground }}
              aria-hidden
            >
              <span
                className={`block size-full text-white ${sourceDisplay.iconClass}`}
              />
            </span>
          ) : null}
          <div className="flex min-w-0 flex-col gap-0">
            <span className="ut-xxs !leading-tight font-normal text-gray-5">
              Posted on
            </span>
            <span className="-mt-px ut-xs !leading-tight font-medium text-link">
              {sourceDisplay.label}
            </span>
          </div>
        </div>
      )}
      {review.review_response && (
        <div className="ml-auto mt-4 max-w-[85%] rounded-l-lg border-r-2 border-primary-darker bg-neutral-50 p-2.5">
          <p className="ut-xs leading-6 font-semibold text-gray-5">
            {brand.companyName}
          </p>
          <p className="ut-sm font-normal text-black-1">
            {review.review_response}
          </p>
        </div>
      )}
    </div>
  );
};

export default Review;
